import React, { useEffect, useMemo, useState } from 'react';
import { commissionAPI } from '../services/api';
import { formatCurrency as fmt } from '../utils/format';

const STATUS_COLOR = {
  full: '#16a34a',
  partial: '#b45309',
  skip: '#6b7280',
  pending: '#7c3aed',
  'n/a': '#6b7280'
};

const STATUS_LABEL = {
  full: '✅ Full Bonus',
  partial: '⚠️ Partial Bonus',
  skip: '✗ No Bonus',
  pending: '⏳ Pending',
  'n/a': '—'
};

function payoutEtaLabel(sale) {
  const readyAt = sale.bonusEta ? new Date(sale.bonusEta) : null;
  const now = new Date();
  if ((sale.bonusStatus === 'full' || sale.bonusStatus === 'partial') && readyAt && readyAt > now) {
    if (sale.payoutPolicy === 'early_full_24_72h') {
      return `⏱ 24-72h (${readyAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})`;
    }
    return `📅 ${readyAt.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })} 1-5`;
  }
  if (sale.bonusStatus === 'full' || sale.bonusStatus === 'partial') return '✅ Released';
  if (sale.bonusStatus !== 'pending') return '—';
  if (!sale.bonusEta) return '—';

  const ready = readyAt || new Date(sale.bonusEta);
  if (sale.payoutPolicy === 'early_full_24_72h') {
    return `⏱ 24-72h (${ready.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})`;
  }

  if (sale.payoutPolicy === 'monthly_next_1_5') {
    return `📅 ${ready.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })} 1-5`;
  }

  return ready.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

function Tile({ label, value, sub, color = '#1f2937', onDetail, hint }) {
  const clickable = typeof onDetail === 'function';
  return (
    <div
      className="stat-tile"
      style={{
        borderLeft: `4px solid ${color}`,
        cursor: clickable ? 'pointer' : undefined,
        transition: 'box-shadow .15s ease, transform .15s ease'
      }}
      onClick={clickable ? onDetail : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDetail(); } } : undefined}
    >
      <div className="stat-label d-flex align-items-center justify-content-between gap-1">
        <span>{label}</span>
        {clickable ? <span style={{ fontSize: 11, opacity: 0.65 }}>ⓘ</span> : null}
      </div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
      {hint ? <div className="stat-sub" style={{ fontSize: 11, opacity: 0.8 }}>{hint}</div> : null}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function LedgerRows({ ledger }) {
  if (!ledger) return <div className="text-muted small mb-0">No ledger row</div>;
  return (
    <ul className="small mb-0 ps-3" style={{ lineHeight: 1.6 }}>
      <li>
        <strong>{ledger.stage}</strong>
        {' · '}{fmt(ledger.amount)}
        {ledger.rateApplied != null && ` @ ${ledger.rateApplied}%`}
        {ledger.isPaid ? <span className="text-success"> · Paid{ledger.paidAt ? ` (${fmtDate(ledger.paidAt)})` : ''}</span> : <span className="text-warning"> · Not paid yet</span>}
      </li>
      {ledger.bonusStatus && ledger.stage === 'bonus' && (
        <li className="text-muted">Bonus status: {ledger.bonusStatus}</li>
      )}
      {ledger.payoutPolicy && (
        <li className="text-muted">
          Payout rule: {ledger.payoutPolicy === 'early_full_24_72h' ? 'Early full (24–72h window)' : 'Monthly (next month 1–5)'}
        </li>
      )}
      {ledger.payoutReadyAt && (
        <li className="text-muted">Ready from: {fmtDate(ledger.payoutReadyAt)}</li>
      )}
      {ledger.computedAt && (
        <li className="text-muted">Computed: {fmtDate(ledger.computedAt)}</li>
      )}
    </ul>
  );
}

function DetailModalShell({ title, children, onClose }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
      }}
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="presentation"
    >
      <div
        className="bg-white rounded-3 shadow-lg"
        style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="d-flex justify-content-between align-items-start p-3 border-bottom">
          <h5 className="mb-0 fw-700" style={{ fontSize: 16 }}>{title}</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

function RequestModal({ maxAmount, onSubmit, onClose }) {
  const [amount, setAmount] = useState(maxAmount);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!amount || Number(amount) < 1) { setError('Valid amount required'); return; }
    if (Number(amount) > maxAmount) { setError(`Max claimable: ${fmt(maxAmount)}`); return; }
    setLoading(true);
    setError('');
    try {
      await onSubmit(Number(amount), note);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Request failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: '90%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
        <h5 className="mb-1">Request Commission Payout</h5>
        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
          Your confirmed unpaid balance: <strong style={{ color: '#16a34a' }}>{fmt(maxAmount)}</strong>
        </p>

        <div className="alert-soft info mb-3" style={{ fontSize: 13 }}>
          Promoter will review your request and transfer the amount.
          Only <strong>confirmed</strong> (2% instant + released bonus) commission can be claimed.
        </div>

        <div className="mb-3">
          <label className="form-label fw-700">Request Amount (₹)</label>
          <input
            type="number"
            className="form-control"
            value={amount}
            min={1}
            max={maxAmount}
            onChange={e => setAmount(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-700">Note (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Any message for the promoter…"
          />
        </div>
        {error && <div className="alert-soft danger mb-2">{error}</div>}
        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-success" onClick={submit} disabled={loading || maxAmount < 1}>
            {loading ? 'Submitting…' : `Request ${fmt(Number(amount) || 0)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyCommission() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState('');
  const [kpiModal, setKpiModal] = useState(null);
  const [saleFocus, setSaleFocus] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await commissionAPI.timeline(12);
      setData(res.data);
      // Auto-expand current month
      if (res.data?.timeline?.length > 0) {
        const last = res.data.timeline[res.data.timeline.length - 1];
        setExpandedMonth(last.month);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Commission data load failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const summary = data?.summary || {};
  const rawWallet = summary.wallet || {};
  const timeline = data?.timeline || [];
  const movements = data?.movements || [];
  const settlements = data?.settlements || [];
  const pendingRequests = summary.pendingRequests || [];
  const unpaidDetails = data?.unpaidDetails || [];
  const currentMonthBlock = timeline[timeline.length - 1] || {};

  const monthKeyInIST = (d) => {
    if (!d) return '';
    const parts = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit'
    }).formatToParts(new Date(d));
    const y = parts.find((p) => p.type === 'year')?.value || '0000';
    const m = parts.find((p) => p.type === 'month')?.value || '01';
    return `${y}-${m}`;
  };

  const wallet = useMemo(() => {
    const currentMonthKey = monthKeyInIST(new Date());
    const availableBalance = Number(rawWallet.availableBalance ?? summary.unpaidConfirmed ?? 0);

    const fallbackCreditRows = movements
      .filter((m) => m.kind === 'credit' && monthKeyInIST(m.at) === currentMonthKey)
      .map((m, idx) => ({
        _id: m.refId || `credit-${idx}`,
        date: m.at,
        amount: Number(m.amount || 0),
        title: m.title || 'Credit',
        detail: m.detail || '—',
        sourceSaleDate: m.sourceSaleDate || null
      }));
    const fallbackPayoutRows = movements
      .filter((m) => m.kind === 'debit' && monthKeyInIST(m.at) === currentMonthKey)
      .map((m, idx) => ({
        _id: m.refId || `debit-${idx}`,
        date: m.at,
        amount: Number(m.amount || 0),
        title: m.title || 'Payout',
        detail: m.detail || '—'
      }));
    const fallbackCredits = fallbackCreditRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const fallbackPayouts = fallbackPayoutRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

    const fallbackPendingRows = timeline.flatMap((month) =>
      (month.sales || [])
        .filter((sale) => sale.bonusStatus === 'pending')
        .map((sale, idx) => ({
          _id: `${month.month}-${idx}`,
          date: sale.saleDate,
          productName: sale.productName || '',
          customerName: sale.customerName || '',
          best: Number(sale.bonusPotential5 || 0),
          min: Number(sale.bonusPotential3 || 0),
          payoutReadyAt: sale.bonusEta || null
        }))
    );
    const fallbackPendingBest = fallbackPendingRows.reduce((s, r) => s + (Number(r.best) || 0), 0);
    const fallbackPendingMin = fallbackPendingRows.reduce((s, r) => s + (Number(r.min) || 0), 0);

    const thisMonthCredits = Number(rawWallet.thisMonthCredits ?? fallbackCredits);
    const thisMonthPayouts = Number(rawWallet.thisMonthPayouts ?? fallbackPayouts);
    const openingRollover = Number(rawWallet.openingRollover ?? (availableBalance - thisMonthCredits + thisMonthPayouts));
    const pendingBonusBest = Number(rawWallet.pendingBonusBest ?? fallbackPendingBest);
    const pendingBonusMin = Number(rawWallet.pendingBonusMin ?? fallbackPendingMin);
    const pendingBonusRows = rawWallet.pendingBonusRows || fallbackPendingRows;

    return {
      availableBalance,
      openingRollover,
      thisMonthCredits,
      thisMonthPayouts,
      pendingBonusBest,
      pendingBonusMin,
      pendingBonusCount: Number(rawWallet.pendingBonusCount ?? pendingBonusRows.length),
      thisMonthCreditRows: rawWallet.thisMonthCreditRows || fallbackCreditRows,
      thisMonthPayoutRows: rawWallet.thisMonthPayoutRows || fallbackPayoutRows,
      pendingBonusRows
    };
  }, [rawWallet, summary.unpaidConfirmed, movements, timeline]);

  const unpaidConfirmed = wallet.availableBalance;
  const walletCreditRows = wallet.thisMonthCreditRows;
  const walletPayoutRows = wallet.thisMonthPayoutRows;
  const pendingBonusRows = wallet.pendingBonusRows;
  const hasPendingRequest = pendingRequests.some(r => r.status === 'pending');

  const handleRequest = async (amount, note) => {
    await commissionAPI.request(amount, note);
    setRequestSuccess(`Request for ${fmt(amount)} submitted successfully! Promoter ko notification milegi.`);
    await load();
  };

  return (
    <div>
      {kpiModal === 'unpaid' && (
        <DetailModalShell title="Available balance — wallet calculation" onClose={() => setKpiModal(null)}>
          <p className="text-muted small mb-3">
            Ye manager wallet ka running calculation hai: opening rollover + is month confirmed credits − is month payouts = available claim-ready balance.
          </p>
          <div className="table-responsive mb-3">
            <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
              <tbody>
                <tr>
                  <td>Opening rollover</td>
                  <td className="text-end text-success fw-700">+{fmt(wallet.openingRollover)}</td>
                </tr>
                <tr>
                  <td>This month credits</td>
                  <td className="text-end text-success fw-700">+{fmt(wallet.thisMonthCredits)}</td>
                </tr>
                <tr>
                  <td>This month payouts</td>
                  <td className="text-end text-danger fw-700">−{fmt(wallet.thisMonthPayouts)}</td>
                </tr>
                <tr style={{ background: '#f0fdf4' }}>
                  <td className="fw-700">Available balance</td>
                  <td className="text-end text-success fw-700">{fmt(unpaidConfirmed)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {unpaidDetails.length === 0 ? (
            <div className="text-muted">Koi pending line nahi — sab settle ho chuka ya abhi claim ke liye amount 0 hai.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stage</th>
                    <th className="text-end">₹</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {unpaidDetails.map(row => (
                    <tr key={row._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.saleDate)}</td>
                      <td><span className="text-uppercase">{row.stage}</span></td>
                      <td className="text-end fw-700 text-success">+{fmt(row.amount)}</td>
                      <td>
                        <div className="fw-600">{row.productName || '—'}</div>
                        <div className="text-muted">{row.customerName || ''}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DetailModalShell>
      )}

      {kpiModal === 'paid' && (
        <DetailModalShell title="Total paid — settlement history" onClose={() => setKpiModal(null)}>
          <p className="text-muted small mb-3">
            Promoter / admin ne jitni baar aapko commission transfer kiya, wo yahan dikhega (recent entries).
          </p>
          {settlements.length === 0 ? (
            <div className="text-muted">Abhi tak koi settlement record nahi.</div>
          ) : (
            <ul className="list-group list-group-flush">
              {settlements.map((s, i) => (
                <li key={i} className="list-group-item px-0 d-flex justify-content-between align-items-center">
                  <span>{fmtDate(s.paidOn)} · <span className="text-capitalize">{s.mode || 'cash'}</span></span>
                  <strong className="text-danger">−{fmt(s.amount)}</strong>
                </li>
              ))}
            </ul>
          )}
        </DetailModalShell>
      )}

      {kpiModal === 'instant' && (
        <DetailModalShell title="This month added — confirmed credits" onClose={() => setKpiModal(null)}>
          <p className="text-muted small mb-3">
            Is month wallet me jitna confirmed commission add hua: 2% instant + released EMI bonus.
          </p>
          {!walletCreditRows.length ? (
            <div className="text-muted">Is month abhi koi confirmed credit nahi.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Title</th>
                    <th>Detail</th>
                    <th className="text-end">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {walletCreditRows.map((row) => (
                    <tr key={row._id}>
                      <td>{fmtDate(row.date)}</td>
                      <td className="fw-600">{row.title}</td>
                      <td className="text-muted">
                        <div>{row.detail}</div>
                        {row.sourceSaleDate ? (
                          <div className="small">Sold on: {fmtDate(row.sourceSaleDate)}</div>
                        ) : null}
                      </td>
                      <td className="text-end text-success fw-700">+{fmt(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-700">
                    <td colSpan={3}>This month added</td>
                    <td className="text-end text-success">+{fmt(wallet.thisMonthCredits)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DetailModalShell>
      )}

      {kpiModal === 'pending' && (
        <DetailModalShell title="Pending bonus pipeline — all months" onClose={() => setKpiModal(null)}>
          <p className="text-muted small mb-3">
            Ye sabhi pending EMI bonus ka future pipeline hai. Final amount EMI close + collection ratio ke baad fix hoga.
          </p>
          {!pendingBonusRows.length ? (
            <div className="text-muted">Abhi koi pending EMI bonus pipeline me nahi.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Product / customer</th>
                    <th className="text-end">5% best</th>
                    <th className="text-end">3% min</th>
                    <th>ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingBonusRows.map((s) => (
                    <tr key={s._id}>
                      <td>{fmtDate(s.date)}</td>
                      <td>
                        <div className="fw-600">{s.productName || '—'}</div>
                        <div className="text-muted">{s.customerName}</div>
                      </td>
                      <td className="text-end">{fmt(s.best)}</td>
                      <td className="text-end">{fmt(s.min)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(s.payoutReadyAt)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-700">
                    <td colSpan={2}>Pipeline total</td>
                    <td className="text-end text-primary">{fmt(wallet.pendingBonusBest)}</td>
                    <td className="text-end text-warning">{fmt(wallet.pendingBonusMin)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DetailModalShell>
      )}

      {saleFocus && (
        <DetailModalShell
          title="Sale — commission movement"
          onClose={() => setSaleFocus(null)}
        >
          <div className="mb-2">
            <div className="fw-700">{saleFocus.sale.productName || '—'}</div>
            <div className="text-muted small">{saleFocus.sale.customerName} · Sale {fmt(saleFocus.sale.saleAmount)} · {fmtDate(saleFocus.sale.saleDate)}</div>
            <div className="text-muted small">Month: {saleFocus.monthLabel}</div>
          </div>
          <div className="mb-3">
            <div className="fw-600 mb-1" style={{ fontSize: 13 }}>Instant (2%)</div>
            <LedgerRows ledger={saleFocus.sale.ledger?.instant} />
          </div>
          <div className="mb-2">
            <div className="fw-600 mb-1" style={{ fontSize: 13 }}>Bonus (EMI / policy)</div>
            <LedgerRows ledger={saleFocus.sale.ledger?.bonus} />
          </div>
          <div className="alert-soft info small mb-0" style={{ fontSize: 12 }}>
            <strong>UI summary:</strong> Instant tile = {fmt(saleFocus.sale.instantAmount)}.
            Bonus status: {STATUS_LABEL[saleFocus.sale.bonusStatus] || saleFocus.sale.bonusStatus}.
            {' '}ETA: {payoutEtaLabel(saleFocus.sale)}.
          </div>
        </DetailModalShell>
      )}

      {showRequest && (
        <RequestModal
          maxAmount={unpaidConfirmed}
          onSubmit={handleRequest}
          onClose={() => setShowRequest(false)}
        />
      )}

      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-700">My Commission</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Manager wallet · rollover balance · monthly audit trail
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
          {unpaidConfirmed > 0 && !hasPendingRequest && (
            <button
              className="btn btn-sm btn-success"
              onClick={() => setShowRequest(true)}
            >
              Request Payout — {fmt(unpaidConfirmed)}
            </button>
          )}
          {hasPendingRequest && (
            <span className="btn btn-sm btn-warning" style={{ cursor: 'default' }}>
              ⏳ Request Pending
            </span>
          )}
        </div>
      </div>

      {error && <div className="alert-soft danger mb-3">{error}</div>}
      {requestSuccess && (
        <div className="alert-soft success mb-3 d-flex justify-content-between">
          <span>{requestSuccess}</span>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setRequestSuccess('')}>✕</button>
        </div>
      )}

      <div className="alert-soft info mb-4" style={{ fontSize: 13, lineHeight: 1.5 }}>
        <strong>Wallet formula:</strong>{' '}
        Opening rollover + <span className="text-success fw-700">this month credits</span> − <span className="text-danger fw-700">payouts</span> = available balance.
        Month-wise report niche audit ke liye hai; upar wale containers manager ka real account status dikhate hain.
      </div>

      {/* ── KPI Tiles ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <Tile
            label="Available balance"
            value={fmt(unpaidConfirmed)}
            sub={`Rollover: ${fmt(wallet.openingRollover)}`}
            color="#16a34a"
            onDetail={() => setKpiModal('unpaid')}
            hint="Tap: full wallet calculation"
          />
        </div>
        <div className="col-6 col-md-3">
          <Tile
            label="Total paid to date"
            value={fmt(summary.totalPaid)}
            sub={`${settlements.length} payouts`}
            color="#0d6efd"
            onDetail={() => setKpiModal('paid')}
            hint="Tap: − payout history"
          />
        </div>
        <div className="col-6 col-md-3">
          <Tile
            label="This month added"
            value={fmt(wallet.thisMonthCredits)}
            sub={`Paid out: ${fmt(wallet.thisMonthPayouts)}`}
            color="#b45309"
            onDetail={() => setKpiModal('instant')}
            hint="Tap: current month + credits"
          />
        </div>
        <div className="col-6 col-md-3">
          <Tile
            label="Pending bonus pipeline"
            value={fmt(wallet.pendingBonusBest)}
            sub={`${wallet.pendingBonusCount || 0} pending · Min ${fmt(wallet.pendingBonusMin)}`}
            color="#7c3aed"
            onDetail={() => setKpiModal('pending')}
            hint="Tap: all future EMI bonus"
          />
        </div>
      </div>

      {/* ── Credit / debit ledger (last 24 months on server) ── */}
      {!loading && movements.length > 0 && (
        <div className="card mb-4" style={{ border: '1px solid #e5e7eb' }}>
          <div className="card-header fw-700 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ fontSize: 14 }}>
            <span>Commission ledger — credits &amp; payouts</span>
            <span className="text-muted small fw-500">Green + credited · Red − paid out</span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Detail</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((row, idx) => (
                  <tr key={`${row.refId}-${row.kind}-${idx}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.at)}</td>
                    <td>
                      {row.kind === 'credit' ? (
                        <span className="badge rounded-pill" style={{ background: 'rgba(22,163,74,0.15)', color: '#15803d' }}>Credit</span>
                      ) : (
                        <span className="badge rounded-pill" style={{ background: 'rgba(220,38,38,0.12)', color: '#b91c1c' }}>Payout</span>
                      )}
                    </td>
                    <td className="fw-600">{row.title}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>{row.detail}</td>
                    <td
                      className={`text-end fw-700 ${row.kind === 'credit' ? 'text-success' : 'text-danger'}`}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {row.kind === 'credit' ? '+' : '−'}{fmt(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── How commission works ── */}
      <div className="card mb-4" style={{ border: '1px solid #e0e7ff', background: '#f5f3ff' }}>
        <div className="card-body py-3">
          <div className="fw-700 mb-2" style={{ color: '#4f46e5', fontSize: 14 }}>💡 Commission System</div>
          <div className="row g-2" style={{ fontSize: 13 }}>
            <div className="col-12 col-md-4">
              <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #16a34a' }}>
                <strong>2% Instant</strong> — Milta hai jab sale hoti hai. Cash ya EMI dono pe.
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #7c3aed' }}>
                <strong>5% Bonus</strong> — EMI puri hone ke baad milta hai (agar 80%+ collection hua).
                Early full closure par payout 24-72h me ready ho sakta hai.
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #b45309' }}>
                <strong>3% Minimum</strong> — Agar collection 50–79% raha to 3% milega. Normal settlement next month 1-5 me hota hai.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Pending Requests Banner ── */}
      {pendingRequests.length > 0 && (
        <div className="alert-soft warning mb-4">
          <strong>⏳ Pending Request:</strong>{' '}
          {fmt(pendingRequests[0].amount)} requested on{' '}
          {new Date(pendingRequests[0].requestedAt).toLocaleDateString('en-IN')} —
          status: <strong>{pendingRequests[0].status}</strong>
        </div>
      )}

      {/* ── Month-by-Month Timeline ── */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <div className="mb-4">
          <div className="fw-700 mb-3" style={{ fontSize: 15 }}>Month-by-Month Breakdown</div>
          {timeline.length === 0 && (
            <div className="text-muted text-center py-4">No commission data yet. Make your first sale!</div>
          )}
          {[...timeline].reverse().map((month) => {
            const isOpen = expandedMonth === month.month;
            const hasActivity = month.salesCount > 0;
            return (
              <div
                key={month.month}
                className="card mb-3"
                style={{ border: '1px solid #e5e7eb', overflow: 'hidden' }}
              >
                {/* Month Header */}
                <div
                  className="card-header d-flex justify-content-between align-items-center py-3"
                  style={{
                    background: hasActivity ? '#f9fafb' : '#fff',
                    cursor: 'pointer',
                    borderBottom: isOpen ? '1px solid #e5e7eb' : 'none'
                  }}
                  onClick={() => setExpandedMonth(isOpen ? null : month.month)}
                >
                  <div>
                    <span className="fw-700" style={{ fontSize: 15 }}>{month.label}</span>
                    {hasActivity && (
                      <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                        {month.salesCount} sale{month.salesCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {hasActivity ? (
                      <>
                        <div className="text-center" style={{ fontSize: 12 }}>
                          <div style={{ color: '#16a34a', fontWeight: 700 }}>{fmt(month.instantEarned)}</div>
                          <div className="text-muted">Instant</div>
                        </div>
                        {month.bonusConfirmed > 0 && (
                          <div className="text-center" style={{ fontSize: 12 }}>
                            <div style={{ color: '#0d6efd', fontWeight: 700 }}>{fmt(month.bonusConfirmed)}</div>
                            <div className="text-muted">Bonus</div>
                          </div>
                        )}
                        {month.bonusPendingCount > 0 && (
                          <div className="text-center" style={{ fontSize: 12 }}>
                            <div style={{ color: '#7c3aed', fontWeight: 700 }}>{fmt(month.bonusPotential5)}</div>
                            <div className="text-muted">{month.bonusPendingCount} pending</div>
                          </div>
                        )}
                        {month.payoutsThisMonth > 0 && (
                          <div className="text-center" style={{ fontSize: 12 }}>
                            <div style={{ color: '#dc2626', fontWeight: 700 }}>−{fmt(month.payoutsThisMonth)}</div>
                            <div className="text-muted">Paid out</div>
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-muted" style={{ fontSize: 12 }}>Koi sale nahi</span>
                    )}
                    <span style={{ fontSize: 18, color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded Sales Detail */}
                {isOpen && (
                  <div className="card-body p-0">
                    {!hasActivity ? (
                      <div className="text-muted text-center py-3 small">Is mahine koi sale nahi hui.</div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f3f4f6' }}>
                                <th>Date</th>
                                <th>Product / Customer</th>
                                <th className="text-end">Sale ₹</th>
                                <th className="text-end" style={{ color: '#16a34a' }}>2% Instant</th>
                                <th>Bonus Status</th>
                                <th className="text-end" style={{ color: '#7c3aed' }}>5% Best</th>
                                <th className="text-end" style={{ color: '#b45309' }}>3% Min</th>
                                <th>ETA</th>
                              </tr>
                            </thead>
                            <tbody>
                              {month.sales.map((sale, idx) => (
                                <tr
                                  key={idx}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setSaleFocus({ sale, monthLabel: month.label })}
                                >
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    {new Date(sale.saleDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                  </td>
                                  <td>
                                    <div className="fw-600" style={{ fontSize: 12 }}>{sale.productName || '—'}</div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>{sale.customerName}</div>
                                  </td>
                                  <td className="text-end">{fmt(sale.saleAmount)}</td>
                                  <td className="text-end fw-700" style={{ color: '#16a34a' }}>{fmt(sale.instantAmount)}</td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: `${STATUS_COLOR[sale.bonusStatus]}20`,
                                      color: STATUS_COLOR[sale.bonusStatus]
                                    }}>
                                      {STATUS_LABEL[sale.bonusStatus] || sale.bonusStatus}
                                    </span>
                                  </td>
                                  <td className="text-end" style={{ color: '#7c3aed', fontWeight: sale.bonusStatus === 'pending' ? 600 : 400 }}>
                                    {sale.bonusStatus === 'full' ? fmt(sale.bonusActual) : fmt(sale.bonusPotential5)}
                                  </td>
                                  <td className="text-end" style={{ color: '#b45309' }}>
                                    {sale.bonusStatus === 'pending' ? fmt(sale.bonusPotential3) : '—'}
                                  </td>
                                  <td style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                    {payoutEtaLabel(sale)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                                <td colSpan={2}>Total</td>
                                <td className="text-end">—</td>
                                <td className="text-end" style={{ color: '#16a34a' }}>{fmt(month.instantEarned)}</td>
                                <td />
                                <td className="text-end" style={{ color: '#7c3aed' }}>
                                  {fmt(month.bonusConfirmed + month.bonusPotential5)}
                                </td>
                                <td className="text-end" style={{ color: '#b45309' }}>
                                  {fmt(month.bonusConfirmed + month.bonusPotential3)}
                                </td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        {/* Month summary bar */}
                        <div className="px-3 py-2" style={{ background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
                          <div className="d-flex gap-4 flex-wrap" style={{ fontSize: 13 }}>
                            <span>Confirmed this month: <strong style={{ color: '#16a34a' }}>{fmt(month.instantEarned + month.bonusConfirmed)}</strong></span>
                            {month.bonusPendingCount > 0 && (
                              <span>
                                Potential bonus waiting: <strong style={{ color: '#7c3aed' }}>{fmt(month.bonusPotential5)}</strong>
                                <span className="text-muted ms-1">(min {fmt(month.bonusPotential3)})</span>
                              </span>
                            )}
                            {month.payoutsThisMonth > 0 && (
                              <span>
                                Promoter payout this month: <strong className="text-danger">−{fmt(month.payoutsThisMonth)}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Payment History ── */}
      {settlements.length > 0 && (
        <div className="card">
          <div className="card-header fw-700" style={{ fontSize: 14 }}>
            💰 Payout history ({settlements.length}) — amounts reduce your running balance
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mode</th>
                  <th className="text-end">Amount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s, i) => (
                  <tr key={i}>
                    <td>{new Date(s.paidOn).toLocaleDateString('en-IN')}</td>
                    <td><span className="chip badge-active">{s.mode || 'cash'}</span></td>
                    <td className="text-end fw-700 text-danger">−{fmt(s.amount)}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{s.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                  <td colSpan={2}>Total paid out</td>
                  <td className="text-end text-danger">−{fmt(summary.totalPaid)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
